/**
 * Project Cost Budget hub — pick a project, open the revision workbench.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AccountBookOutlined, SearchOutlined } from '@ant-design/icons';
import { projectKeys } from '@/hooks/useProjects';
import { projectService, type Project } from '@/services/project.service';

const { Title, Text } = Typography;

export const ProjectBudgetHubPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: projects = [], isLoading } = useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectService.getProjects(),
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const code = String(p.project_number || '').toLowerCase();
      const name = String(p.project_name || '').toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [projects, search]);

  const columns: ColumnsType<Project> = [
    {
      title: 'Code',
      dataIndex: 'project_number',
      width: 140,
      render: (v) => v || '—',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      render: (v) => v || '—',
    },
    {
      title: 'Status',
      dataIndex: 'project_status',
      width: 120,
      render: (v) => (v ? <Tag>{String(v)}</Tag> : '—'),
    },
    {
      title: 'Type',
      dataIndex: 'project_type',
      width: 100,
      render: (v) => v || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      render: (_, row) => (
        <Button
          type="primary"
          icon={<AccountBookOutlined />}
          onClick={() => navigate(`/prj/projects/${row.id}/budgets`)}
        >
          Cost Budget
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Project Cost Budget
          </Title>
          <Text type="secondary">
            Original and Current cost budgets with revision control — open a project workbench to edit
            lines, print, import Excel, seed from Project WBS, or open Budget vs Actual.
          </Text>
        </div>

        <Card size="small">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by project code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360, marginBottom: 16 }}
          />
          <Table<Project>
            rowKey={(r) => r.id}
            loading={isLoading}
            dataSource={filtered}
            columns={[
              ...columns.slice(0, -1),
              {
                title: '',
                key: 'actions',
                width: 280,
                render: (_, row) => (
                  <Space>
                    <Button
                      type="primary"
                      icon={<AccountBookOutlined />}
                      onClick={() => navigate(`/prj/projects/${row.id}/budgets`)}
                    >
                      Cost Budget
                    </Button>
                    <Button
                      onClick={() => navigate(`/prj/projects/${row.id}/budgets/variance`)}
                    >
                      Vs Actual
                    </Button>
                  </Space>
                ),
              },
            ]}
            pagination={{ pageSize: 20 }}
            size="middle"
          />
        </Card>
      </Space>
    </div>
  );
};

export default ProjectBudgetHubPage;
